/**
 * Reader-time study event log + snapshot capture hook.
 *
 * Mirrors the backend's `/api/study/*` capture pipeline (server stamps
 * `server_ts`; client only contributes the event payload and a skew-only
 * `client_ts`). The hook is mounted on every case-viewer regardless of
 * study mode (React rules of hooks); pass `null` for ctx when the current
 * case isn't on a study schedule and every method becomes a no-op.
 *
 * One hook instance per case-viewer mount; `viewer_session_id` is
 * generated fresh on mount and the `event_seq` counter starts at 0.
 *
 * Backend: a2zrai/web-demo-backend#338.
 */
import { useCallback, useMemo, useRef } from "react";
import axios from "axios";

import { getApiBaseUrl } from "../config/api";

// ============================================================================
// Type vocabulary — must match `EventType` Literal in study_endpoints.py.
// Adding a new event type requires a code change here AND on the backend.
// ============================================================================

export type StudyEventType =
  | "case_opened"
  | "first_interaction"
  | "timer_started"
  | "timer_paused"
  | "timer_resumed"
  | "report_signed"
  | "ai_panel_opened"
  | "ai_suggestion_accepted"
  | "ai_suggestion_rejected"
  | "ai_suggestion_edited"
  | "survey_skipped";

export type StudyArm = "with_ai" | "without_ai";
export type StudyCohort = "A" | "B";
// Final snapshots are owned exclusively by the atomic signStudy operation.
export type SnapshotKind = "initial";
export type Difficulty = 1 | 2 | 3 | 4 | 5;

export interface StudyContext {
  scheduleId: string;
  studyUid: string;
  arm: StudyArm;
  cohort: StudyCohort;
  /**
   * When true, this is a practice case — all log/sign/snapshot calls are
   * no-ops so practice data never enters the study dataset. The timer and
   * UI still run normally so the rad sees the full experience during warmup.
   */
  isPractice: boolean;
}

export type ReaderReportContext = Pick<StudyContext, "scheduleId" | "studyUid" | "isPractice">;

export interface StudyEventLog {
  /** Append one event. Resolves immediately when ctx is null (no-op). */
  logEvent: (eventType: StudyEventType, payload?: Record<string, unknown>) => Promise<void>;
  /** Capture an initial report snapshot. No-op when ctx is null. */
  logSnapshot: (
    kind: SnapshotKind,
    reportHtml: string,
    templateId: string,
    templateCharCount: number,
  ) => Promise<void>;
  /**
   * Atomic close — final snapshot + `report_signed` event in one
   * server-side TransactWriteItems. Resolves only after both land.
   * Rejects on network failure / 4xx / 5xx so callers can block the
   * UI sign signal until the audit-trail row is persisted. No-op when
   * ctx is null.
   *
   * `activeReadingMs` is the idle-subtracted active reading time in
   * milliseconds (integer). This is the primary endpoint measurement;
   * the server cross-checks it against the timer_started/paused/resumed
   * event reconstruction.
   */
  signStudy: (
    expectedRevision: string,
    templateId: string,
    templateCharCount: number,
    difficulty: Difficulty,
    activeReadingMs: number,
  ) => Promise<void>;
  /**
   * SILENT wall-clock timing for a PRACTICE (untimed) case — case open → sign.
   * The inverse of every other method: it fires ONLY for practice (no-op when
   * ctx is null or not practice), because practice cases post nothing else and
   * this lands in a backend namespace disjoint from the study dataset. The
   * reader-facing UI is unchanged — this is invisible instrumentation.
   */
  logPracticeTiming: (params: {
    wallClockMs: number;
    clientOpenTs: string;
    clientSignTs: string;
  }) => Promise<void>;
}

// ============================================================================
// Constants
// ============================================================================

// Surfaced into every event for traceability. Vite injects from package.json
// at build time when `VITE_APP_VERSION` is set; falls back to "dev" locally
// so we still record a stable identifier.
const APP_VERSION =
  (import.meta.env as Record<string, string | undefined>).VITE_APP_VERSION ??
  "web-demo-frontend@dev";

// ============================================================================
// Hook
// ============================================================================

export function useStudyEventLog(ctx: StudyContext | null): StudyEventLog {
  // crypto.randomUUID is standard on all modern browsers including the ones
  // demo-frontend targets. Ref so it survives re-renders but starts fresh on
  // each case-viewer mount.
  const sessionIdRef = useRef<string | null>(null);
  if (sessionIdRef.current === null) {
    sessionIdRef.current = crypto.randomUUID();
  }
  const seqRef = useRef(0);
  // Retain a single sign request through an uncertain response. Reconstructing
  // it would change eventSeq/timing and turn a retry into a second signature.
  const pendingSignRef = useRef<{
    scope: string;
    report: string;
    body: Record<string, unknown>;
    inFlight?: Promise<void>;
  } | null>(null);

  // Snapshot ctx onto a ref so the returned callbacks stay stable across ctx
  // identity changes — consumers depend on `studyLog` in effect deps and a
  // fresh callback would re-fire case_opened every time the parent rebuilds
  // the assignment object on background refetches.
  const ctxRef = useRef<StudyContext | null>(ctx);
  ctxRef.current = ctx;

  const buildEnvelope = useCallback(() => {
    const current = ctxRef.current;
    if (current === null) return null;
    const seq = seqRef.current;
    seqRef.current += 1;
    return {
      scheduleId: current.scheduleId,
      studyUid: current.studyUid,
      arm: current.arm,
      cohort: current.cohort,
      eventSeq: seq,
      clientTs: new Date().toISOString(),
      appVersion: APP_VERSION,
      viewerSessionId: sessionIdRef.current!,
    };
  }, []);

  const logEvent = useCallback<StudyEventLog["logEvent"]>(
    async (eventType, payload = {}) => {
      // null ctx → no-op (not in study mode); isPractice → no-op (practice data
      // excluded). Check BEFORE buildEnvelope so practice/no-study calls don't
      // burn event_seq numbers — the first real event must start at seq 0.
      const current = ctxRef.current;
      if (current === null || current.isPractice) return;
      const envelope = buildEnvelope();
      if (envelope === null) return;
      const body = { ...envelope, eventType, payload };
      await axios.post(`${getApiBaseUrl()}/api/study/event`, body, {
        withCredentials: true,
      });
    },
    [buildEnvelope],
  );

  const logSnapshot = useCallback<StudyEventLog["logSnapshot"]>(
    async (kind, reportHtml, templateId, templateCharCount) => {
      const current = ctxRef.current;
      if (current === null || current.isPractice) return;
      await axios.post(
        `${getApiBaseUrl()}/api/study/snapshot`,
        {
          scheduleId: current.scheduleId,
          studyUid: current.studyUid,
          kind,
          reportHtml,
          templateId,
          templateCharCount,
        },
        { withCredentials: true },
      );
    },
    [],
  );

  const signStudy = useCallback<StudyEventLog["signStudy"]>(
    async (expectedRevision, templateId, templateCharCount, difficulty, activeReadingMs) => {
      // null ctx → no-op; isPractice → no-op (practice data excluded). Check
      // BEFORE buildEnvelope so a practice sign doesn't burn an event_seq.
      const current = ctxRef.current;
      if (current === null || current.isPractice) return;
      if (!expectedRevision) throw new Error("Save the reader report before signing.");
      const scope = JSON.stringify([
        current.scheduleId,
        current.studyUid,
        current.arm,
        current.cohort,
      ]);
      const report = JSON.stringify([templateId, templateCharCount, difficulty, expectedRevision]);
      let pending = pendingSignRef.current;
      if (!pending || pending.scope !== scope) {
        const envelope = buildEnvelope();
        if (envelope === null) return;
        pending = {
          scope,
          report,
          body: {
            ...envelope,
            templateId,
            templateCharCount,
            difficultySelfRated: difficulty,
            activeReadingMs,
            expectedRevision,
            operationId: crypto.randomUUID(),
          },
        };
        pendingSignRef.current = pending;
      }
      if (pending.report !== report)
        throw new Error("Retry signing the original report before changing it.");
      if (!pending.inFlight) {
        const attempt = pending;
        attempt.inFlight = axios
          .post(`${getApiBaseUrl()}/api/study/sign`, attempt.body, { withCredentials: true })
          .then(() => undefined)
          .catch((error: unknown) => {
            // These statuses reject before commit. Network/5xx/409 outcomes
            // remain pinned to the original operation until reconciled.
            if (
              axios.isAxiosError(error) &&
              [400, 401, 403, 404, 422].includes(error.response?.status ?? 0) &&
              pendingSignRef.current === attempt
            ) {
              pendingSignRef.current = null;
            }
            throw error;
          })
          .finally(() => {
            attempt.inFlight = undefined;
          });
      }
      await pending.inFlight;
    },
    [buildEnvelope],
  );

  const logPracticeTiming = useCallback<StudyEventLog["logPracticeTiming"]>(
    async ({ wallClockMs, clientOpenTs, clientSignTs }) => {
      // Inverse gate: fires ONLY for practice. A non-practice call would write
      // a PRACTICE_TIMING row for a real case (contaminating the dataset), so
      // guard defensively here in addition to the caller's isPractice check.
      const current = ctxRef.current;
      if (current === null || !current.isPractice) return;
      const body = {
        scheduleId: current.scheduleId,
        studyUid: current.studyUid,
        arm: current.arm,
        cohort: current.cohort,
        wallClockMs,
        clientOpenTs,
        clientSignTs,
        appVersion: APP_VERSION,
        viewerSessionId: sessionIdRef.current!,
      };
      await axios.post(`${getApiBaseUrl()}/api/study/practice-timing`, body, {
        withCredentials: true,
      });
    },
    [],
  );

  // Memoize the returned object so consumers depending on `studyLog` in
  // useEffect/useCallback dep arrays don't re-fire on every render. Without
  // this, a parent's "fire case_opened when this changes" effect re-runs on
  // every render and resets state-machine flags.
  return useMemo(
    () => ({ logEvent, logSnapshot, signStudy, logPracticeTiming }),
    [logEvent, logSnapshot, signStudy, logPracticeTiming],
  );
}
